import { Fragment } from "react";

/**
 * Copy with emphasis, and copy that runs to more than one paragraph.
 *
 * The parent surface has three or four passages long enough that a wall of text loses the reader,
 * and one — the waiting-claims section — where the numbers are the point and a parent should not
 * have to hunt for them in a sentence.
 *
 * **Why a marker rather than JSX in the copy table.** Every Persian string lives in `copy.ts` so it
 * can be run through the lexicon guard and read end to end by a reviewer; the lexicon's v0→v1 note
 * puts a native-speaking child clinician on that job. JSX would put half of each sentence in a
 * component file and make that review impossible. `**…**` reads fine in the source, survives the
 * guard as ordinary text, and is what a reviewer already expects emphasis to look like.
 *
 * No HTML is parsed and nothing is injected — the string is split and the pieces become React
 * children — so this cannot become an injection surface even if copy ever came from elsewhere.
 */

/** Space above every paragraph but the first. Static strings, so Tailwind can see them. */
const GAP = { sm: "mt-sm", md: "mt-md", lg: "mt-lg" } as const;

/** Splits on `**` and wraps the alternate pieces in `<strong>`. */
export function Emphasise({ text }: { text: string }) {
  const pieces = text.split("**");
  return (
    <>
      {pieces.map((piece, i) =>
        i % 2 === 1 ? (
          <strong key={i} className="font-bold">
            {piece}
          </strong>
        ) : (
          <Fragment key={i}>{piece}</Fragment>
        ),
      )}
    </>
  );
}

/**
 * A passage: one paragraph or several, with emphasis honoured.
 *
 * Paragraphs are separate strings rather than newlines inside one, because a paragraph break is
 * structure — encoding it as whitespace means every renderer has to agree on how to split it back
 * out, and one of them eventually will not.
 */
export function Passage({
  text,
  className,
  gap = "md",
}: {
  text: string | readonly string[];
  /** Utilities for every paragraph. There is no style escape hatch, on purpose. */
  className?: string;
  /** Space between paragraphs, as a scale step rather than a number. */
  gap?: "sm" | "md" | "lg";
}) {
  const paragraphs = typeof text === "string" ? [text] : text;
  return (
    <>
      {paragraphs.map((paragraph, i) => (
        <p
          key={i}
          // `text-body` is the default and any caller class overrides it, because a later
          // utility wins. The gap is only applied from the second paragraph on.
          className={`${className ?? "text-body"} ${i === 0 ? "" : GAP[gap]}`}
        >
          <Emphasise text={paragraph} />
        </p>
      ))}
    </>
  );
}
